/**
 * Centralized Affiliate Tracking Service
 * Handles affiliate ID detection, storage, and URL building
 */

class AffiliateService {
  constructor() {
    this.AFFILIATE_ID_KEY = 'affiliateId';
    this.AFFILIATE_SESSION_KEY = 'affiliateSessionId';
  }

  /**
   * Extract affiliate ID from URL query parameters
   * @param {string} search - URL search string (e.g., "?affiliateId=123&other=value")
   * @returns {string|null} - The affiliate ID or null if not found
   */
  extractAffiliateIdFromUrl(search) {
    if (!search) return null;
    
    const queryParams = new URLSearchParams(search);
    return queryParams.get('affiliateId');
  }

  /**
   * Get the current affiliate ID from sessionStorage
   * @returns {string|null} - The stored affiliate ID or null
   */
  getStoredAffiliateId() {
    try {
      return sessionStorage.getItem(this.AFFILIATE_ID_KEY);
    } catch (error) {
      console.warn('Failed to get affiliate ID from sessionStorage:', error);
      return null;
    }
  }

  /**
   * Store affiliate ID in sessionStorage
   * @param {string} affiliateId - The affiliate ID to store
   */
  storeAffiliateId(affiliateId) {
    if (!affiliateId) return;
    
    try {
      sessionStorage.setItem(this.AFFILIATE_ID_KEY, affiliateId);
      // Also store a session timestamp to track when the affiliate was first detected
      sessionStorage.setItem(this.AFFILIATE_SESSION_KEY, Date.now().toString());
    } catch (error) {
      console.warn('Failed to store affiliate ID in sessionStorage:', error);
    }
  }

  /**
   * Clear stored affiliate ID
   */
  clearAffiliateId() {
    try {
      sessionStorage.removeItem(this.AFFILIATE_ID_KEY);
      sessionStorage.removeItem(this.AFFILIATE_SESSION_KEY);
    } catch (error) {
      console.warn('Failed to clear affiliate ID from sessionStorage:', error);
    }
  }

  /**
   * Get the current affiliate ID (from URL or sessionStorage)
   * @param {string} search - URL search string
   * @returns {string|null} - The affiliate ID or null
   */
  getCurrentAffiliateId(search) {
    // First, check if there's an affiliate ID in the current URL
    const urlAffiliateId = this.extractAffiliateIdFromUrl(search);
    if (urlAffiliateId) {
      // Store it for future use
      this.storeAffiliateId(urlAffiliateId);
      return urlAffiliateId;
    }

    // If no affiliate ID in URL, return the stored one
    return this.getStoredAffiliateId();
  }

  /**
   * Build a URL with affiliate ID if available
   * @param {string} baseUrl - The base URL (e.g., "/visa-type/israel/usa")
   * @param {string} search - Current URL search string (optional)
   * @returns {string} - URL with affiliate ID appended if available
   */
  buildUrlWithAffiliate(baseUrl, search = '') {
    const affiliateId = this.getCurrentAffiliateId(search);
    
    if (!affiliateId) {
      return baseUrl;
    }

    // Check if URL already has query parameters
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}affiliateId=${affiliateId}`;
  }

  /**
   * Build a URL with affiliate ID from stored value only
   * @param {string} baseUrl - The base URL
   * @returns {string} - URL with stored affiliate ID appended if available
   */
  buildUrlWithStoredAffiliate(baseUrl) {
    const affiliateId = this.getStoredAffiliateId();
    
    if (!affiliateId) {
      return baseUrl;
    }

    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}affiliateId=${affiliateId}`;
  }

  /**
   * Check if affiliate ID has expired (optional feature)
   * Note: With sessionStorage, this is less critical as it clears automatically
   * @param {number} expirationHours - Hours after which affiliate ID expires (default: 24)
   * @returns {boolean} - True if affiliate ID is still valid
   */
  isAffiliateIdValid(expirationHours = 24) {
    try {
      const sessionTimestamp = sessionStorage.getItem(this.AFFILIATE_SESSION_KEY);
      if (!sessionTimestamp) return false;

      const sessionTime = parseInt(sessionTimestamp);
      const expirationTime = sessionTime + (expirationHours * 60 * 60 * 1000);
      
      return Date.now() < expirationTime;
    } catch (error) {
      console.warn('Failed to check affiliate ID validity:', error);
      return false;
    }
  }

  /**
   * Get affiliate tracking info for analytics
   * @returns {object} - Object containing affiliate tracking information
   */
  getAffiliateTrackingInfo() {
    const affiliateId = this.getStoredAffiliateId();
    const sessionTimestamp = sessionStorage.getItem(this.AFFILIATE_SESSION_KEY);
    
    return {
      affiliateId,
      sessionTimestamp: sessionTimestamp ? parseInt(sessionTimestamp) : null,
      isValid: this.isAffiliateIdValid(),
      timeSinceFirstVisit: sessionTimestamp ? Date.now() - parseInt(sessionTimestamp) : null
    };
  }
}

// Create and export a singleton instance
const affiliateService = new AffiliateService();
export default affiliateService;
